import {
  Box,
  BoxProps,
  InputWrapper,
  SingleSelect,
  TextInput,
  xsBumps,
} from '@wrisk/ui-components'
import { isEmpty, isNil, negate } from 'lodash-es'
import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  tEntries,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'

export interface CancellationReasonInputProps extends BoxProps {
  name?: string
  onChange: (value: string | null) => void
  value: string | null
  reasons: string[]
}

const tKey = TKey('components.cancellation-reason-input')

export const CancellationReasonInput: FunctionComponent<CancellationReasonInputProps> = ({
  name,
  onChange,
  reasons,
  value,
  ...props
}) => {
  const { t } = useWriskTranslation()

  const [[reason, other], setCancellationReason] = useState(value?.split('|') ?? [])

  const cancellationReasons = useMemo(
    () =>
      reasons.reduce(
        (acc, reason) => ({
          ...acc,
          [reason]: t(tEntries('cancellationReasons', reason)),
        }),
        {},
      ),
    [reasons, t],
  )

  useEffect(() => {
    reason === 'Other'
      ? isEmpty(other)
        ? onChange(null)
        : onChange([reason, other].join('|'))
      : onChange(reason)
  }, [reason, other, onChange])

  const onReasonChange = useCallback(
    (reason: string) => setCancellationReason(([, prevOther]) => [reason, prevOther]),
    [],
  )

  const onOtherChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) =>
      setCancellationReason(([prevReason]) => [prevReason, target.value]),
    [],
  )

  return (
    <Box id={name} width={1} {...props}>
      <SingleSelect
        width={1}
        name={[name, 'reason'].filter(negate(isNil)).join('-')}
        onChange={onReasonChange}
        value={reason}
        options={cancellationReasons}
      />
      {reason === 'Other' && (
        <Box width={1} mt={xsBumps}>
          <InputWrapper>
            <TextInput
              name={[name, 'other'].filter(negate(isNil)).join('-')}
              type='text'
              variant='ghost'
              width={1}
              placeholder={t(tKey('placeholder-other'))}
              value={other ?? ''}
              onChange={onOtherChange}
            />
          </InputWrapper>
        </Box>
      )}
    </Box>
  )
}
